import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ChevronDown } from "lucide-react";
import { buildQuoteChoices, linkedGroupLeaders, resolveLinkedSelections, itemLabel, priceOf, type ChoiceItem, type QuoteClientSelections } from "@/lib/quoteChoices";
import { ActivityCard } from "@/components/quote/ActivityCard";

interface Props {
  items: ChoiceItem[];
  selections: QuoteClientSelections;
  onChange?: (s: QuoteClientSelections) => void;
  currencySymbol: string;
  baseTotal: number;
  adjustment: number;
  equivalents?: (amount: number) => string | null;
  /** Full quote total for a hypothetical selection (discounts, fees, markup applied). */
  priceForSelection?: (s: QuoteClientSelections) => number;
  pax?: number;
  mode?: "hotels" | "activities" | "optional" | "all";
  showTotals?: boolean;
}

const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Expanded details render with the standard itinerary ActivityCard, so an
 * optional extra or alternative option looks identical to any other item on
 * the quote (images, description, inclusions / exclusions, room details).
 */
function ItemDetails({ item }: { item: any }) {
  const description = (item?.custom_description || item?.description || "").trim();
  const inclusions = ((item?.inclusions || []) as string[]).filter((l) => l?.trim());
  const exclusions = ((item?.exclusions || []) as string[]).filter((l) => l?.trim());
  const hasImages = !!(item?.manual_image_url || item?.products?.hero_image_url || item?.suppliers?.hero_image_url);

  if (!description && !hasImages && !inclusions.length && !exclusions.length) {
    return <p className="text-sm text-muted-foreground">No further details available for this item.</p>;
  }

  return <ActivityCard item={item} />;
}

export function QuoteChoicesPanel({ items, selections: rawSelections, onChange, currencySymbol, baseTotal, adjustment, equivalents, priceForSelection, pax, mode = "all", showTotals }: Props) {
  const built = buildQuoteChoices(items);
  const selections = resolveLinkedSelections(items, rawSelections);
  const leaders = linkedGroupLeaders(built.groups);
  const leaderName = (pid: string) => {
    const lg = built.groups.find((x) => x.primary.id === leaders.get(pid));
    return lg?.primary?.item_type === "accommodation" ? "hotel" : lg ? itemLabel(lg.primary) : "";
  };
  const isHotel = (g: any) => g.primary?.item_type === "accommodation";
  const groups = mode === "hotels" ? built.groups.filter(isHotel) : mode === "activities" ? built.groups.filter((g) => !isHotel(g)) : mode === "optional" ? [] : built.groups;
  const optional = mode === "all" || mode === "optional" ? built.optional : [];
  const footer = showTotals ?? (mode === "all" || mode === "optional");
  const title = mode === "hotels" ? "Choose your hotel" : mode === "activities" ? "Choose your activity" : mode === "optional" ? "Optional extras" : "Tailor your programme";
  const subtitle = mode === "hotels" ? (readOnlyText(onChange, "Hotel options available on this quote.", "Select your preferred hotel — the total updates as you choose."))
    : mode === "activities" ? readOnlyText(onChange, "Alternative activities available on this quote.", "Switch between alternative activities — the total updates as you choose.")
    : mode === "optional" ? readOnlyText(onChange, "Optional items, not included in the base total.", "Add optional items — the total updates as you choose.") : null;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  if (!optional.length && !groups.length) return null;
  const readOnly = !onChange;
  const signed = (n: number) => `${n >= 0 ? "+" : "−"}${currencySymbol}${fmt(Math.abs(n))}`;
  const withAlt = (primaryId: string, optId: string): QuoteClientSelections => ({
    ...selections,
    alternative_selections: { ...selections.alternative_selections, [primaryId]: optId },
  });
  const withOptional = (id: string, on: boolean): QuoteClientSelections => ({
    ...selections,
    optional_item_ids: on
      ? Array.from(new Set([...selections.optional_item_ids, id]))
      : selections.optional_item_ids.filter((x) => x !== id),
  });

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandButton = (id: string) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleExpanded(id);
      }}
      className="flex items-center gap-1 text-xs text-primary hover:underline print:hidden"
      aria-expanded={expanded.has(id)}
    >
      {expanded.has(id) ? "Hide details" : "View details"}
      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded.has(id) ? "rotate-180" : ""}`} />
    </button>
  );

  return (
    <div className="mb-8 rounded-lg border bg-muted/20 p-5 print:break-inside-avoid">
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {subtitle ?? (readOnly
          ? "Options available on this quote. They are not included in the base total."
          : "Add optional extras or switch between options — the total updates as you choose.")}
      </p>

      {groups.map((g) => {
        const chosen = selections.alternative_selections[g.primary.id] || g.primary.id;
        return (
          <div key={g.primary.id} className="mb-4">
            {mode === "all" && !leaders.has(g.primary.id) && <p className="text-sm font-medium mb-2">Choose one</p>}
            {leaders.has(g.primary.id) && (
              <p className="text-xs text-muted-foreground mb-2">Follows your {leaderName(g.primary.id)} choice.</p>
            )}
            <RadioGroup
              value={chosen}
              disabled={readOnly || leaders.has(g.primary.id)}
              onValueChange={(v) =>
                onChange?.({ ...selections, alternative_selections: { ...selections.alternative_selections, [g.primary.id]: v } })
              }
              className="space-y-2"
            >
              {[g.primary, ...g.alternatives].map((opt, idx) => {
                const optTotal = priceForSelection ? priceForSelection(withAlt(g.primary.id, opt.id)) : null;
                const primaryTotal = priceForSelection ? priceForSelection(withAlt(g.primary.id, g.primary.id)) : null;
                const delta = optTotal != null && primaryTotal != null ? optTotal - primaryTotal : priceOf(opt) - priceOf(g.primary);
                const isOpen = expanded.has(opt.id);
                return (
                  <div key={opt.id} className="rounded-md border bg-background">
                    <Label htmlFor={`opt-${opt.id}`} className="flex items-center gap-3 p-3 cursor-pointer font-normal">
                      <RadioGroupItem id={`opt-${opt.id}`} value={opt.id} />
                      <span className="flex-1">
                        <span className="font-medium">Option {idx + 1}: {itemLabel(opt)}</span>
                        <span className="block mt-0.5">{expandButton(opt.id)}</span>
                      </span>
                      <span className="text-sm text-muted-foreground whitespace-nowrap text-right">
                        {optTotal != null && (
                          <span className="block font-semibold text-foreground">{currencySymbol}{fmt(optTotal)} total</span>
                        )}
                        {optTotal != null && pax && pax > 0 && (
                          <span className="block text-xs">{currencySymbol}{fmt(optTotal / pax)} per person</span>
                        )}
                        {optTotal != null && pax && pax > 0 && equivalents?.(optTotal / pax) && (
                          <span className="block text-xs">{equivalents(optTotal / pax)} per person</span>
                        )}
                        <span className="block text-xs">
                          {idx === 0 ? "Included" : Math.abs(delta) < 0.005 ? "Same price" : delta < 0 ? `Saves ${currencySymbol}${fmt(-delta)}` : `Adds ${currencySymbol}${fmt(delta)}`}
                        </span>
                        {optTotal != null
                          ? equivalents?.(optTotal) && <span className="block text-xs">{equivalents(optTotal)}</span>
                          : idx > 0 && equivalents?.(delta) && <span className="block text-xs">{equivalents(delta)}</span>}
                      </span>
                    </Label>
                    {isOpen && (
                      <div className="border-t px-3 py-3">
                        <ItemDetails item={opt} />
                      </div>
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        );
      })}

      {optional.length > 0 && (
        <div className="space-y-2">
          {mode !== "optional" && <p className="text-sm font-medium">Optional extras</p>}
          {optional.map((o) => {
            const on = selections.optional_item_ids.includes(o.id);
            const isOpen = expanded.has(o.id);
            return (
              <div key={o.id} className="rounded-md border bg-background">
                <div className="flex items-center gap-3 p-3">
                  <Switch
                    id={`optx-${o.id}`}
                    checked={on}
                    disabled={readOnly}
                    onCheckedChange={(c) =>
                      onChange?.({
                        ...selections,
                        optional_item_ids: c
                          ? [...selections.optional_item_ids, o.id]
                          : selections.optional_item_ids.filter((x) => x !== o.id),
                      })
                    }
                  />
                  <span className="flex-1">
                    <Label htmlFor={`optx-${o.id}`} className="font-normal cursor-pointer">{itemLabel(o)}</Label>
                    <span className="block mt-0.5">{expandButton(o.id)}</span>
                  </span>
                  {(() => {
                    const d = priceForSelection
                      ? priceForSelection(withOptional(o.id, true)) - priceForSelection(withOptional(o.id, false))
                      : priceOf(o);
                    return (
                      <span className="text-sm whitespace-nowrap text-right">
                        {signed(d)}
                        {equivalents?.(d) && <span className="block text-xs text-muted-foreground">{equivalents(d)}</span>}
                      </span>
                    );
                  })()}
                </div>
                {isOpen && (
                  <div className="border-t px-3 py-3">
                    <ItemDetails item={o} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {footer && !readOnly && adjustment !== 0 && (
        <div className="mt-4 border-t pt-3 text-sm space-y-1">
          <div className="flex justify-between"><span>Base investment</span><span>{currencySymbol}{fmt(baseTotal)}</span></div>
          <div className="flex justify-between"><span>Your selections</span><span>{signed(adjustment)}</span></div>
          <div className="flex justify-between font-semibold"><span>Adjusted total</span><span>{currencySymbol}{fmt(baseTotal + adjustment)}</span></div>
        </div>
      )}
    </div>
  );
}

function readOnlyText(onChange: unknown, ro: string, rw: string) {
  return onChange ? rw : ro;
}
